import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TbArrowLeft, TbBrandWhatsapp, TbInfoCircle } from 'react-icons/tb';
import { GUIAS, findGuia, formatUpdated } from '../data/guias';
import { OFFICE, SITE_URL, getTelLink } from '../data/escritorio';
import { JsonLd, usePageMeta } from '../utils/seo';
import LawyerChooser from '../components/LawyerChooser';
// Estilos base (.nvh-*) vêm de Home.css.
import './Home.css';
import './Guia.css';

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <p className="nvg-p">{block.text}</p>;
    case 'list':
      return (
        <ul className="nvg-list">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case 'cards':
      return (
        <div className="nvg-cards">
          {block.items.map((item) => (
            <div key={item.title} className="nvg-card">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      );
    case 'steps':
      return (
        <ol className="nvg-steps">
          {block.items.map((item, index) => (
            <li key={item.strong}>
              <span className="nvg-step-number" aria-hidden="true">{index + 1}</span>
              <span>
                <strong>{item.strong}</strong> {item.text}
              </span>
            </li>
          ))}
        </ol>
      );
    default:
      return null;
  }
};

const Guia = () => {
  const { slug } = useParams();
  const guia = findGuia(slug);
  const [chooserOpen, setChooserOpen] = useState(false);
  const closeChooser = useCallback(() => setChooserOpen(false), []);

  usePageMeta(
    guia
      ? { title: guia.title, description: guia.description }
      : { title: 'Guia não encontrado', noindex: true }
  );

  if (!guia) {
    return (
      <div className="nvh nvg">
        <section className="nvg-hero">
          <div className="nvh-wrap">
            <h1>Guia não encontrado</h1>
            <p className="nvg-subtitle">O endereço pode ter mudado.</p>
            <Link to="/guias" className="nvh-btn nvh-btn-gold nvg-back">
              <TbArrowLeft aria-hidden="true" />
              Ver todos os guias
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const others = GUIAS.filter((g) => g.slug !== guia.slug);
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guia.title,
    description: guia.description,
    inLanguage: 'pt-BR',
    datePublished: guia.updated,
    dateModified: guia.updated,
    mainEntityOfPage: `${SITE_URL}/guias/${guia.slug}`,
    author: { '@type': 'Organization', name: OFFICE.name, url: SITE_URL },
    publisher: { '@type': 'Organization', name: OFFICE.name, url: SITE_URL }
  };

  return (
    <div className="nvh nvg nvg-guia">
      <JsonLd data={articleJsonLd} />

      <section className="nvg-hero">
        <div className="nvh-wrap">
          <nav className="nvg-breadcrumb" aria-label="Caminho">
            <Link to="/">Início</Link>
            <span aria-hidden="true">›</span>
            <Link to="/guias">Guias</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">{guia.cardTitle}</span>
          </nav>
          <h1>{guia.title}</h1>
          <p className="nvg-subtitle">{guia.subtitle}</p>
          <div className="nvg-meta">
            Leitura de {guia.readingTime} minutos · Atualizado em {formatUpdated(guia.updated)}
          </div>
        </div>
      </section>

      <section className="nvg-body">
        <div className="nvh-wrap nvg-layout">
          <article className="nvg-article">
            <div className="nvg-summary">
              <div className="nvh-eyebrow">Em resumo</div>
              <ul>
                {guia.summary.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            {guia.sections.map((section) => (
              <div key={section.title} className="nvg-section">
                <h2>{section.title}</h2>
                {section.blocks.map((block, index) => (
                  <Block key={index} block={block} />
                ))}
              </div>
            ))}

            <p className="nvg-disclaimer">
              <TbInfoCircle aria-hidden="true" />
              Este guia tem caráter informativo e não substitui a análise do caso concreto por um advogado.
            </p>
          </article>

          <aside className="nvg-aside">
            <div className="nvg-cta">
              <div className="nvh-eyebrow nvh-eyebrow-gold">Plantão 24 horas</div>
              <h2>Precisa de orientação agora?</h2>
              <p>Atendemos {OFFICE.plantao.charAt(0).toLowerCase() + OFFICE.plantao.slice(1)}</p>
              <button type="button" className="nvh-btn nvh-btn-gold" onClick={() => setChooserOpen(true)}>
                <TbBrandWhatsapp aria-hidden="true" />
                Falar com um advogado
              </button>
              <a className="nvg-cta-phone" href={getTelLink(OFFICE.phone)}>
                ou ligue {OFFICE.phone}
              </a>
            </div>
            <div className="nvg-others">
              <div className="nvh-eyebrow">Outros guias</div>
              <ul>
                {others.map((g) => (
                  <li key={g.slug}>
                    <Link to={`/guias/${g.slug}`}>{g.cardTitle}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <div className="nvg-bar">
        <div className="nvg-bar-text">
          <strong>Plantão 24 horas</strong>
          <span>Flagrantes e urgências</span>
        </div>
        <button type="button" className="nvh-btn nvh-btn-gold" onClick={() => setChooserOpen(true)}>
          <TbBrandWhatsapp aria-hidden="true" />
          Falar agora
        </button>
      </div>

      <LawyerChooser open={chooserOpen} onClose={closeChooser} />
    </div>
  );
};

export default Guia;
